
import { Component, Prop, Mixins, Provide } from "vue-property-decorator";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import Context from "@/mixins/context";
import { DataModules } from "@/store/modules/data";
import { DefaultTaxTemplateSorters } from "@/data/sorters/tax/index";
import { IFilter, ISorter } from "@/models/tables";
import { ConfirmModalModes } from "@/data/enums";
import { ITaxTemplate } from "@/models/tax";
import { CreateElement } from "vue";
import { DefaultTaxTemplateFilters } from "@/data/filters/tax/index";
import {
  ITaxTemplatesProvider,
  ITaxTemplatesProviderMethods
} from "@/models/providers/tax";
import { ApiOperators } from "@/data/table";

@Component
export default class TaxTemplatesProvider extends Mixins(
  FilteringSortingTableStore,
  Context
) {
  // Props

  @Prop({ default: "TTP" /* Tax Templates Provider */ })
  public declare id: string;

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => ({}) })
  public declare manualFilters: Record<string, string | number | boolean>;

  @Prop({ default: () => DefaultTaxTemplateFilters })
  public declare availableFilters: (vm: TaxTemplatesProvider) => IFilter[];

  @Prop({ default: () => DefaultTaxTemplateSorters })
  public declare availableSorters: (vm: TaxTemplatesProvider) => ISorter[];

  // Data
  public dataModule: DataModules = DataModules.TAX_TEMPLATES;
  public debounceSearch = this.$_.debounce(this.doQuickSearch, 250);

  public isLoading = true;
  public isReloading = false;
  public filters: IFilter[] = [];
  public sorters: ISorter[] = [];
  public searchQuery = "";

  // Lifecycle methods
  public async created() {
    if (!this.contentIsAvailable) return;

    this.filters = this.availableFilters(this);
    this.sorters = this.availableSorters(this);
    this.loadData();
  }

  // Getters
  public get canDelete() {
    return this.$userCan("delete_taxtemplate");
  }

  public get guardFunctionalities() {
    return ["list_taxtemplates"];
  }

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public get hasTaxTemplates() {
    return !!this.table.data.length;
  }

  public get queryFilter() {
    return this.searchQuery
      ? { [`filter[name|${ApiOperators.LIKE}]`]: `%${this.searchQuery}%` }
      : {};
  }

  // Methods
  public async loadData() {
    try {
      this.isLoading = true;
      await this.getData({ ignoreStored: this.isReloading });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.cancelRequests();
    return this.loadData();
  }

  public doQuickSearch(value = "") {
    if (value === this.searchQuery) return Promise.resolve();
    this.searchQuery = value;
    return this.reloadData();
  }

  // Provide

  @Provide("taxTemplatesProviderMethods")
  public get providerMethods(): ITaxTemplatesProviderMethods {
    return {
      getData: this.getData,
      doQuickSearch: this.doQuickSearch,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      reloadData: this.reloadData,
      debounceSearch: this.debounceSearch,
      openEditModal: this.openEditModal,
      deleteTaxTemplate: this.deleteTaxTemplate
    };
  }

  @Provide("taxTemplatesProvider")
  public providerData(): ITaxTemplatesProvider {
    return {
      availableFilters: this.filters,
      availableSorters: this.sorters,
      constQueryFilters: this.constQueryFilters,
      canDelete: this.canDelete,
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      hasTaxTemplates: this.hasTaxTemplates,
      isLoading: this.table.loading || this.isLoading,
      isReloading: this.isReloading,
      limit: this.table.limit,
      page: this.table.page,
      taxTemplates: this.table.data as ITaxTemplate[],
      searchQuery: this.searchQuery,
      table: this.table,
      total: this.table.total,
      guardFunctionalities: this.guardFunctionalities
    };
  }

  public openEditModal(taxTemplate: ITaxTemplate) {
    this.$store.dispatch(`data/${DataModules.TAX_TEMPLATES}/openAddEditModal`, {
      parent: this,
      props: {
        taxTemplateId: taxTemplate.id
      },
      events: {
        success: () => {
          this.reloadData();
        }
      }
    });
  }

  // Override mixin method
  public getStoreParams(params: any): Promise<any> {
    return this.$_.merge(
      {
        params: {
          ...this.queryFilter
        }
      },
      params // Important – must merge last
    );
  }

  public async deleteTaxTemplate(taxTemplateId: ITaxTemplate["id"]) {
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          mode: ConfirmModalModes.BUTTON,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_confirmation", {
            object: (this.$t("_.tax_template") as string).toLowerCase()
          })
        },
        events: {
          confirmed: async () => {
            const taxTemplate = this.table.data?.find(
              i => i.id === taxTemplateId
            );
            await this.deleteTaxTemplateConfirmed(taxTemplate);
            confirm.close();
          }
        }
      }
    });
  }

  public deleteTaxTemplateConfirmed(taxTemplate: ITaxTemplate) {
    return this.$store
      .dispatch(`data/${this.dataModule}/remove`, { id: taxTemplate.id })
      .then(() => {
        this.getData({ ignoreStored: true });
      })
      .catch(error => {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      });
  }

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });
    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: () => this.getData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $taxTemplatesData: this.providerData(),
        $taxTemplatesMethods: this.providerMethods
      })
    );
  }
}
